'use client';

/**
 * DashboardSocialView — the chain-dashboard rendering of the social feed ("Spine & Index").
 *
 * One spatial model for BOTH states: the curated feed is always the left spine (the page's
 * single scroll), and the right column swaps content — RosterRail (browse) ↔
 * SocialEntityInspector (focus) — WITHOUT ever moving the feed (kills the old side-swap).
 * The feed renders in normal page flow (no inner scroll container) so the page is the one
 * true scroller; only the right column is sticky+bounded, so the rail's footer is always
 * reachable. On narrow viewports the right column collapses to a horizontal roster strip on
 * top (focus → the inspector slots in-flow above the feed).
 *
 * All token-native (gold-only). The standalone `/social` page does NOT use this — it keeps
 * its existing layout in ClientSocialPage; this is gated behind the `inDashboard` flag there.
 */

import { Eyebrow, Mono, useIsMobile } from '@/components/dashboard/atoms';
import { useScrollEdgeFade } from '@/hooks/useScrollEdgeFade';
import RosterRail from './RosterRail';
import SocialEntityInspector from './SocialEntityInspector';
import InterceptsFeed from './InterceptsFeed';
import { DeskRead, TapeScrubber, ArchiveBanner } from './DeskRead';
import type { DeskTapeDay } from './desk-tape';
import type { EntitySearchData } from '@/types/tags';

interface DashboardSocialViewProps {
  entities: EntitySearchData[];
  /** UNfiltered roster — resolves the desk read's @tokens even while the feed filter narrows `entities`. */
  allEntities?: EntitySearchData[];
  isLoadingEntities: boolean;
  currentEntity: EntitySearchData | null;
  associatedTags: any[];
  selectedHandle: string;
  onHandleChange: (handle: string) => void;
  onEntitySelect: (entityId: string, entitySlug: string) => void;
  onClear: () => void;
  chainLabel?: string;
  chainColor?: string;
  chainHandles?: string[];
  /**
   * True while the host page's in-place feed filter is narrowing things down.
   * When set, `chainHandles` is ALREADY filtered — an empty array means "no tracked
   * accounts match" (render the empty state), NOT "show the unscoped firehose".
   */
  feedFilterActive?: boolean;
  directoryHref: string;
  socialBase: string;
  activeHandles: number;
  latestUpdate: string;
  /** Pre-built filter control (input + type chips + live count) from the host page. */
  searchSlot: React.ReactNode;
  /** THE DESK READ + THE TAPE — published recap days, newest first (server-fetched). */
  deskTape?: DeskTapeDay[];
  /** YYYY-MM-DD the tape is scrubbed to (undefined = live/today). */
  selectedDay?: string;
  /** Scrub THE TAPE (null = jump to today). */
  onDaySelect?: (day: string | null) => void;
}

export default function DashboardSocialView({
  entities,
  allEntities,
  isLoadingEntities,
  currentEntity,
  associatedTags,
  selectedHandle,
  onHandleChange,
  onEntitySelect,
  onClear,
  chainLabel,
  chainColor,
  chainHandles,
  feedFilterActive = false,
  directoryHref,
  socialBase,
  activeHandles,
  latestUpdate,
  searchSlot,
  deskTape,
  selectedDay,
  onDaySelect,
}: DashboardSocialViewProps) {
  // Collapse the right rail to a horizontal strip whenever the grid is single-column
  // (below the lg=1024 breakpoint the 12-col grid stacks).
  const stacked = useIsMobile(1024);

  // The feed renders ONCE (InterceptsFeed). Its handles + header reflect focus state;
  // the focus narrowing is passed as explicit props below.
  const focused = !!currentEntity;
  // Filter matched nothing → show an explicit empty state. NEVER hand the feed an
  // empty handle list (it treats that as "no filter" and shows the global firehose).
  const noFilterMatches = !focused && feedFilterActive && (chainHandles?.length ?? 0) === 0;

  const feedTitle = focused ? (
    <div className="flex flex-col">
      <div className="flex items-center gap-2">
        <Eyebrow color="var(--slate)">Focused Feed</Eyebrow>
      </div>
      <span style={{ marginTop: 3, fontFamily: 'var(--font-sans)', fontSize: 18, fontWeight: 700, letterSpacing: '-0.02em', color: 'var(--parchment)' }}>
        {currentEntity!.name}
      </span>
      <Mono size={11} color="var(--slate)" style={{ marginTop: 2 }}>
        {selectedHandle === 'all'
          ? `From ${currentEntity!.twitterHandles.length} tracked ${currentEntity!.twitterHandles.length === 1 ? 'account' : 'accounts'}`
          : `Showing @${selectedHandle}`}
      </Mono>
    </div>
  ) : (
    <div className="flex flex-col">
      <div className="flex items-center gap-2">
        <Eyebrow>Curated Feed</Eyebrow>
        <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--green-text)' }} aria-hidden />
      </div>
      <span style={{ marginTop: 3, display: 'flex', alignItems: 'center', gap: 7 }}>
        {chainColor && (
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: chainColor, flexShrink: 0 }} aria-hidden />
        )}
        <span style={{ fontFamily: 'var(--font-sans)', fontSize: 18, fontWeight: 700, letterSpacing: '-0.02em', color: 'var(--parchment)' }}>
          {chainLabel ? `${chainLabel} Feed` : 'All Tweets'}
        </span>
      </span>
      <Mono size={11} color="var(--slate)" style={{ marginTop: 2 }}>
        {feedFilterActive
          ? `Filtered · ${chainHandles?.length ?? 0} matched ${(chainHandles?.length ?? 0) === 1 ? 'account' : 'accounts'}`
          : `${activeHandles} tracked ${activeHandles === 1 ? 'account' : 'accounts'} · updated ${latestUpdate}`}
      </Mono>
    </div>
  );

  // Scroll-edge fades for both bounded scrollports (the market rail treatment —
  // useScrollEdgeFade): edges feather only when content continues past them, so the
  // tuner/inspector never hard-slices against the feed's full-height scroll.
  const browseRail = useScrollEdgeFade<HTMLDivElement>([focused, isLoadingEntities, stacked]);
  const focusRail = useScrollEdgeFade<HTMLDivElement>([focused, currentEntity?.id, selectedHandle]);

  // ── THE DESK READ + THE TAPE (v3) — the page LEADS with the agent's newest
  // conclusion (the latest published recap, verbatim), with the temporal record
  // scrubber attached beneath it. Both degrade to nothing when no recap exists.
  const latestRead = deskTape && deskTape.length > 0 ? deskTape[0] : null;
  const todayUtc = new Date().toISOString().slice(0, 10);
  const archiveRead = selectedDay ? deskTape?.find((d) => d.date === selectedDay) : undefined;

  return (
    <div className="space-y-5">
      {/* ══ THE LEAD — conclusion first (THE LAW), then the record. Full width:
          the desk speaks before any chrome (filter, roster, sort) appears. ══ */}
      {latestRead && (
        <div>
          <DeskRead
            read={latestRead}
            isToday={latestRead.date === todayUtc}
            entities={allEntities ?? entities}
            onEntitySelect={onEntitySelect}
          />
          <TapeScrubber
            tape={deskTape!}
            selectedDay={selectedDay}
            onSelectDay={(day) => onDaySelect?.(day)}
          />
        </div>
      )}

      {/* Spine & Index grid. The chain h1 + meta row come from the server route's
          CompactPageHeader; the page's prime top band is intentionally left clear.

          DOM order: the FEED (the page's primary content) is FIRST in source order so
          crawlers/screen-readers reach it before the index, and so the mobile single-
          column stack reads feed-first (Lam's default stack: feed, then roster). On lg
          the feed sits LEFT and the roster/tuner RIGHT (2026-06-12 — matches the
          market page's content-left / rail-right convention).

          The filter+search tool lives in the ROSTER (index) column header region — it
          sits WITH the roster it scopes, above the roster's own header (tracked count +
          Active/A–Z toggle), NOT above the feed. The FEED is the first thing in its
          column (its title/freshness row stays). */}
      <div className="flex flex-col lg:grid lg:grid-cols-12 lg:gap-6 lg:items-start">
        {/* MOBILE-ONLY filter strip — the cut wants the type-chip strip + filter input
            ABOVE the feed on ≤sm, with the roster BELOW the feed. On lg this copy is
            hidden (display:none) because the index column below carries the real,
            roster-scoping searchSlot. order-1 keeps it first in the mobile stack. */}
        <div className="order-1 mb-4 lg:hidden">{searchSlot}</div>

        {/* Feed column — visually LEFT (lg:order-1, the market-page convention: content
            left, rail right); on mobile it's order-2, i.e. directly under the filter
            strip and ABOVE the roster. Renders in normal page flow (no inner scroll);
            the page is the one true scroller. */}
        <div className="order-2 mb-6 lg:mb-0 lg:col-span-8 lg:order-1">
          {/* ARCHIVE banner — names the scrubbed day's read of record (THE TAPE). */}
          {selectedDay && (
            <div style={{ marginBottom: 14 }}>
              <ArchiveBanner
                date={selectedDay}
                read={archiveRead}
                onJumpToToday={() => onDaySelect?.(null)}
              />
            </div>
          )}
          {noFilterMatches ? (
            <div
              style={{
                background: 'var(--surface)',
                border: '0.5px solid var(--divider)',
                borderRadius: 6,
                padding: '28px 20px',
                textAlign: 'center',
              }}
            >
              <Mono size={12} color="var(--parchment-dim)">
                No tracked accounts match your filter.
              </Mono>
              <Mono size={11} color="var(--slate)" style={{ display: 'block', marginTop: 6 }}>
                Clear the filter to restore the full feed.
              </Mono>
            </div>
          ) : (
            <InterceptsFeed
              chainHandles={chainHandles}
              chainLabel={chainLabel}
              dayFilter={selectedDay}
              focusedSlug={focused ? currentEntity!.slug : undefined}
              focusedHandles={
                focused
                  ? selectedHandle === 'all'
                    ? currentEntity!.twitterHandles
                    : [selectedHandle]
                  : undefined
              }
              focusedName={focused ? currentEntity!.name : undefined}
              onClearFocus={onClear}
              onEntitySelect={onEntitySelect}
              entitySocialBase={socialBase}
              title={feedTitle}
            />
          )}
        </div>

        {/* Index column — visually RIGHT (lg:order-2, matching the market page's rail
            side); on mobile order-3 stacks it BELOW the feed. Roster (browse) or
            inspector (focus).

            BROWSE = THE TUNER (intercepts prototype, mirrored right): filter + type
            chips + the condensed ranked roster are ONE flat sticky scrollport with its
            own thin scrollbar — the rail scrolls as a unit, the column is separated
            from the feed by a single left divider, and scroll-edge fades (the market
            rail treatment) feather the clip lines against the feed's full-height
            scroll. On mobile the order-1 strip above the feed carries the filter and
            the roster collapses to a horizontal chip strip (no sticky, no divider). */}
        {/* lg:self-stretch is LOAD-BEARING: the grid is items-start, which would
            shrink this column to its content height and leave the sticky child no
            travel room (it would just scroll away with the page — the old bug).
            Stretching the column to the row height (= the feed's) lets the rail
            actually stick beside the full-height feed. */}
        <div className="order-3 lg:col-span-4 lg:order-2 lg:self-stretch lg:border-l-[0.5px] lg:border-divider lg:pl-5">
          {focused ? (
            <div
              ref={focusRail.ref}
              onScroll={focusRail.onScroll}
              className="lg:sticky lg:top-[68px] lg:max-h-[calc(100vh-84px)] lg:overflow-y-auto lg:overscroll-contain"
              style={{ scrollbarWidth: 'thin', scrollbarColor: 'var(--divider) transparent' }}
            >
              <div className="mb-4 min-h-[96px] hidden lg:block">{searchSlot}</div>
              <SocialEntityInspector
                entity={currentEntity!}
                selectedHandle={selectedHandle}
                onHandleChange={onHandleChange}
                associatedTags={associatedTags}
                onEntitySelect={onEntitySelect}
                onClear={onClear}
              />
              {/* Discovery NEVER stops (2026-06-12): the roster rides BELOW the focus
                  card — picking the next entity is one click, no clear-first step.
                  Mobile keeps the horizontal chip strip under the inspector. */}
              <div className="mt-5">
                <RosterRail
                  entities={entities}
                  onEntitySelect={onEntitySelect}
                  currentEntityId={currentEntity!.id}
                  directoryHref={directoryHref}
                  horizontal={stacked}
                />
              </div>
            </div>
          ) : (
            <div
              ref={browseRail.ref}
              onScroll={browseRail.onScroll}
              className="lg:sticky lg:top-[68px] lg:max-h-[calc(100vh-84px)] lg:overflow-y-auto lg:overscroll-contain"
              style={{ scrollbarWidth: 'thin', scrollbarColor: 'var(--divider) transparent' }}
            >
              {/* Filter + chips — the tuner's head (lg only; reserved height so the
                  skeleton→live swap never reflows the roster below). */}
              <div className="mb-4 min-h-[96px] hidden lg:block">{searchSlot}</div>
              {isLoadingEntities ? (
                <RosterSkeleton />
              ) : (
                <RosterRail
                  entities={entities}
                  onEntitySelect={onEntitySelect}
                  currentEntityId={undefined}
                  directoryHref={directoryHref}
                  horizontal={stacked}
                />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function RosterSkeleton() {
  // Mirrors the flat tuner roster (head line + condensed 36px rows), not a card box.
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }} aria-hidden>
      <div style={{ height: 12, width: '45%', background: 'var(--surface-2)', borderRadius: 3 }} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {Array.from({ length: 10 }).map((_, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, height: 36, padding: '0 8px' }}>
            <div style={{ width: 16, height: 9, borderRadius: 2, background: 'var(--surface-2)', flexShrink: 0 }} />
            <div style={{ width: 20, height: 20, borderRadius: 4, background: 'var(--surface-2)', flexShrink: 0 }} />
            <div style={{ height: 11, flex: 1, background: 'var(--surface-2)', borderRadius: 3 }} />
          </div>
        ))}
      </div>
    </div>
  );
}
